export type Daily = {
  date: string
  sum: number
}

export type DailyTotals = {
  customers_count: number
} & Daily

export type Monthly = {
  month: number
  year: number
  sum: number
}

export type MonthlyTotals = {
  customers_count: number
} & Monthly

export type Group<DataDepTimeMode> = {
  customer_type: 'Учащиеся' | 'Сотрудники' | 'Гость'
  total: number
  data: Array<DataDepTimeMode>
  klass_id?: number
  klass_title?: string
}

export type School<DataDepTimeMode> = {
  school_id: number
  school_title: string
  total: number
  data: Array<DataDepTimeMode>
}

export type Purchase = {
  id: 1576631
  customer_type: 'Student' | 'Employee'
  school_title: string
  klass_title: string
  customer_name: {
    last: string | null
    first: string | null
    middle: string | null
  }
  customer_account_number: string | null
  customer_phone: string | null
  sale_type: 'complex' | 'personal'
  time: string
  sum: number
  items: Array<{
    title: string
    cost: string
    n: number
    is_complex: boolean
    sold_complex_items: Array<{
      title: string
      cost: number | null
      portion: string | null
      index: number
    }>
  }>
}

export type Item = {
  title: string
  cost: number
  portion: string
  n: number
  sum: number
}

export type ComplexCamp = {
  title: string
  sum: number
  count: number
}

export type Complex =
  | {
      title: string
      cost: number
      portion: string | null
      items: Array<{
        title: string
        cost: number | null
        portion: string | null
        index: number
      }>
      n: number
      sum: number
    }
  | ComplexCamp

export type SummaryCamp = Array<{
  title: string
  sum: number
  count: number
}>

export type Summary =
  | {
      purchases_sum: number
      subsidies_sum: number
      customers_count: number
      // Summary Special
      common_subsidies_sum?: number // сумма дотаций по общей справке
      common_subsidies_count?: number
      personal_subsidies_sum?: number // сумма дотаций по индивидуальной справке, кроме СОП и ОВЗ
      personal_subsidies_count?: number
      SOP_subsidies_sum?: number // сумма дотаций по справке СОП
      SOP_subsidies_count?: number
      OVZ_subsidies_sum?: number // сумма дотаций по справке ОВЗ
      OVZ_subsidies_count?: number
      purchases_count?: number
      subsidies_count?: number
      ukr_subsidies_sum?: number
      contracts_subsidies_sum?: number
      mobilized_subsidies_sum?: number
    }
  | SummaryCamp

export const timeModes = Object.freeze({
  Daily: 'daily',
  Monthly: 'monthly',
})

export type TimeMode = (typeof timeModes)[keyof typeof timeModes]

type ReportsDataPurchasesResultTemplate<
  TM = TimeMode,
  DataDepTimeMode = Daily | Monthly,
  DataTotalsDepTimeMode = DailyTotals | MonthlyTotals,
> = {
  summary: Summary
  time_mode: TM
  totals?: Array<DataTotalsDepTimeMode>
  groups?: Array<Group<DataDepTimeMode>>
  purchases?: Array<Purchase>
  schools?: Array<School<DataDepTimeMode>>
  items?: Array<Item>
  complexes?: Array<Complex>
  school_ids: Array<number>
  details_group:
    | null
    | 'by_group_complexes'
    | 'by_groups_and_types'
    | 'by_purchases'
    | 'by_items_and_complexes'
    | 'by_items_and_complexes_and_cost'
  exclude_OVZ?: boolean
}

export type ReportsDataPurchasesResultDaily = ReportsDataPurchasesResultTemplate<
  'daily',
  Daily,
  DailyTotals
>

export type ReportsDataPurchasesResultMonthly = ReportsDataPurchasesResultTemplate<
  'monthly',
  Monthly,
  MonthlyTotals
>

export type ReportsDataPurchasesResult =
  | ReportsDataPurchasesResultDaily
  | ReportsDataPurchasesResultMonthly
  | { details_group: 'by_product_type' }
  | {
      details_group: 'by_payment_method'
      summary: Array<{ title: string; sum: number; n: number }>
      dates: Array<string>
      payment_methods_by_school: Array<{
        school_id: number
        title: string
        items: Array<{
          key: string
          title: string
          data_by_date: Record<string, { count: number; sum: number }>
          total: { count: number; sum: number }
        }>
        total: { count: number; sum: number }
      }>
      payment_methods: Record<
        'internal' | 'sbp' | 'bank_card' | 'cash',
        {
          data_by_date: Record<string, { count: number; sum: number }>
          total: { count: number; sum: number }
        }
      >
      grand_total: {
        data_by_date: Record<string, { count: number; sum: number }>
        total: { count: number; sum: number }
      }
    }

export const byPaymentMethodDateFormat = 'YYYY-MM-DD' as const
